import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { EQUIPMENT_MODELS_ENDPOINT, PARTS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useTenantSettings } from "../../hooks/useTenantSettings";
import { useHasPermission } from "../../contexts/auth";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import CustomAutocomplete from "../ui/customAutocomplete/customAutocomplete.component";
import { DECIMAL_INPUT_REGEX, formatDecimalForInput, formatQuantity, toApiDecimal } from "../../utils";

/**
 * How a job invoice prices the part. A part stores at most one of a markup and
 * a fixed price; with neither, the workspace's default markup applies - so the
 * form picks the mode first and only ever submits the field that belongs to it.
 */
type PricingMode = "default" | "markup" | "fixed";

interface EquipmentModelOption {
  id: number | string;
  display_name?: string;
  name?: string;
}

/** The page kinds a part can wear with, as the Part fields name them. */
const WEAR_FIELDS = [
  ["wears_black_pages", "Black pages"],
  ["wears_color_pages", "Color pages"],
  ["wears_scanned_pages", "Scanned pages"],
] as const;

type WearField = (typeof WEAR_FIELDS)[number][0];

interface PartFormValues {
  name: string;
  unit: string;
  code: string;
  notification_threshold: string;
  pricing_mode: PricingMode;
  /** Percentage added to the purchase price, instead of the workspace's. */
  markup: string;
  /** Net price one unit is invoiced at, whatever it was bought for. */
  sale_price_net: string;
  /** Pages the part lasts; its current price over them is added to the page
   * cost of the models it is used in. */
  lifespan_pages: string;
  wears_black_pages: boolean;
  wears_color_pages: boolean;
  wears_scanned_pages: boolean;
  equipment_models: EquipmentModelOption[];
}

type DecimalField = "notification_threshold" | "markup" | "sale_price_net";

const defaultValues: PartFormValues = {
  name: "",
  unit: "",
  code: "",
  notification_threshold: "",
  pricing_mode: "default",
  markup: "",
  sale_price_net: "",
  lifespan_pages: "",
  wears_black_pages: true,
  wears_color_pages: true,
  wears_scanned_pages: false,
  equipment_models: [],
};

const pricingModeOf = (part: { sale_price_net?: unknown; markup?: unknown }): PricingMode => {
  if (part.sale_price_net !== null && part.sale_price_net !== undefined) return "fixed";
  if (part.markup !== null && part.markup !== undefined) return "markup";
  return "default";
};

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object" && first && "message" in first) {
      return (first as { message?: string }).message ?? null;
    }
  }
  if (typeof value === "object" && value && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

interface PartFormProps {
  id?: string;
  onSuccess?: (data?: any) => void;
}

export default function PartForm({ id, onSuccess }: PartFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading } = useCRUD(PARTS_ENDPOINT);
  // Only for naming the workspace markup in the picker - a viewer who cannot
  // read the settings still gets a working form, just without the figure.
  const { settings: tenantSettings } = useTenantSettings();
  // The page cost inputs feed profitability, so they are only shown to whoever
  // can see it. The form still submits the values it loaded: the backend resets
  // a lifespan left out of the request.
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  const [formData, setFormData] = useState<PartFormValues>(defaultValues);
  const [serverErrors, setServerErrors] = useState<any>(null);
  const [loadingInitial, setLoadingInitial] = useState(!!id);
  const [fetchedModelOptions, setFetchedModelOptions] = useState<EquipmentModelOption[]>([]);

  useEffect(() => {
    if (!id) {
      setLoadingInitial(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const response = await get(id);
      if (cancelled) return;
      if (response?.data) {
        const d = response.data;
        setFormData({
          name: d.name ?? "",
          unit: d.unit ?? "",
          code: d.code ?? "",
          notification_threshold: formatDecimalForInput(d.notification_threshold),
          pricing_mode: pricingModeOf(d),
          markup: formatDecimalForInput(d.markup),
          sale_price_net: formatDecimalForInput(d.sale_price_net),
          lifespan_pages: d.lifespan_pages == null ? "" : String(d.lifespan_pages),
          wears_black_pages: d.wears_black_pages ?? true,
          wears_color_pages: d.wears_color_pages ?? true,
          wears_scanned_pages: d.wears_scanned_pages ?? false,
          equipment_models: d.equipment_models ?? [],
        });
      }
      setLoadingInitial(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, get]);

  const getFieldError = (field: string): string | null =>
    extractErrorMessage(serverErrors?.[field]);

  const handleChange = (field: "name" | "unit" | "code") => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleDecimalChange = (field: DecimalField) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = e.target.value;
    if (nextValue === "" || DECIMAL_INPUT_REGEX.test(nextValue)) {
      setFormData((prev) => ({ ...prev, [field]: nextValue }));
    }
  };

  const handleModelOptionsChange = useCallback((options: EquipmentModelOption[]) => {
    setFetchedModelOptions((prev) => {
      const merged = [...prev];
      options.forEach((option) => {
        if (!merged.find((existing) => String(existing.id) === String(option.id))) {
          merged.push(option);
        }
      });
      return merged;
    });
  }, []);

  const modelOptions = [...formData.equipment_models, ...fetchedModelOptions].filter(
    (option, index, all) => all.findIndex((item) => String(item.id) === String(option.id)) === index
  );

  const handleWearChange = (field: WearField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleSubmit = async () => {
    setServerErrors(null);
    const { pricing_mode: pricingMode } = formData;

    // An empty value in the chosen mode would quietly fall back to the
    // workspace markup - not what someone who picked a mode meant.
    const pricingField = pricingMode === "markup" ? "markup" : pricingMode === "fixed" ? "sale_price_net" : null;
    if (pricingField && !formData[pricingField]) {
      setServerErrors({ [pricingField]: t("Enter a value or choose the workspace markup.") });
      return;
    }

    const payload = {
      name: formData.name,
      unit: formData.unit,
      code: formData.code || undefined,
      notification_threshold: formData.notification_threshold
        ? toApiDecimal(formData.notification_threshold)
        : undefined,
      markup: pricingMode === "markup" ? toApiDecimal(formData.markup) : null,
      sale_price_net: pricingMode === "fixed" ? toApiDecimal(formData.sale_price_net) : null,
      lifespan_pages: formData.lifespan_pages ? Number(formData.lifespan_pages) : null,
      wears_black_pages: formData.wears_black_pages,
      wears_color_pages: formData.wears_color_pages,
      wears_scanned_pages: formData.wears_scanned_pages,
      equipment_models: formData.equipment_models.map((model) => model.id),
    };

    const response = id ? await update({ id, ...payload }) : await create(payload);

    if (response?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(response.data);
    } else {
      setServerErrors(response?.errorData);
      const failureMessage =
        extractErrorMessage(response?.errorData?.message) ||
        extractErrorMessage(response?.errorData?.detail) ||
        t("There was an error, please review the form.");
      enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
    }
  };

  if (loadingInitial) return <CustomCircularProgress />;

  const fieldBoxSx = {
    flex: { xs: "1 1 100%", sm: "1 1 45%" },
    maxWidth: { xs: "100%", sm: "calc(50% - 8px)" },
  };
  const workspaceMarkupLabel = tenantSettings
    ? t("Workspace markup ({{markup}}%)", { markup: formatQuantity(tenantSettings.default_part_markup) })
    : t("Workspace markup");

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Name")}
          value={formData.name}
          onChange={handleChange("name")}
          required
          fullWidth
          inputProps={{ maxLength: 200 }}
          error={Boolean(getFieldError("name"))}
          helperText={getFieldError("name") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Unit")}
          value={formData.unit}
          onChange={handleChange("unit")}
          required
          fullWidth
          inputProps={{ maxLength: 20 }}
          error={Boolean(getFieldError("unit"))}
          helperText={getFieldError("unit") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Code")}
          value={formData.code}
          onChange={handleChange("code")}
          fullWidth
          inputProps={{ maxLength: 100 }}
          error={Boolean(getFieldError("code"))}
          helperText={getFieldError("code") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Notification Threshold")}
          value={formData.notification_threshold}
          onChange={handleDecimalChange("notification_threshold")}
          fullWidth
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          error={Boolean(getFieldError("notification_threshold"))}
          helperText={getFieldError("notification_threshold") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          select
          size="small"
          label={t("Price on job invoices")}
          value={formData.pricing_mode}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, pricing_mode: e.target.value as PricingMode }));
            setServerErrors(null);
          }}
          fullWidth
          helperText={
            formData.pricing_mode === "default"
              ? t("Purchase price plus the workspace markup from the invoicing settings.")
              : formData.pricing_mode === "markup"
                ? t("Purchase price plus this part's own markup.")
                : t("Always this price, whatever the part was bought for.")
          }
        >
          <MenuItem value="default">{workspaceMarkupLabel}</MenuItem>
          <MenuItem value="markup">{t("Own markup")}</MenuItem>
          <MenuItem value="fixed">{t("Fixed price")}</MenuItem>
        </TextField>
      </Box>
      {formData.pricing_mode === "markup" && (
        <Box sx={fieldBoxSx}>
          <TextField
            size="small"
            label={t("Markup")}
            value={formData.markup}
            onChange={handleDecimalChange("markup")}
            required
            fullWidth
            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            error={Boolean(getFieldError("markup"))}
            helperText={
              getFieldError("markup") ||
              t("Added to the purchase price of the delivery the part is used from.")
            }
          />
        </Box>
      )}
      {formData.pricing_mode === "fixed" && (
        <Box sx={fieldBoxSx}>
          <TextField
            size="small"
            label={t("Fixed sale price (net)")}
            value={formData.sale_price_net}
            onChange={handleDecimalChange("sale_price_net")}
            required
            fullWidth
            inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
            InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
            error={Boolean(getFieldError("sale_price_net"))}
            helperText={getFieldError("sale_price_net") || ""}
          />
        </Box>
      )}
      {canViewProfitability && (
        <Box sx={{ width: "100%", border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 1 }}>
          <Typography variant="h6" sx={{ mb: 0.5, color: "var(--textColor)" }}>
            {t("Page cost calculation")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "With a lifespan, the part's current price divided by it is added to the page cost of every machine of the models below that has no page cost of its own."
            )}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={fieldBoxSx}>
              <TextField
                size="small"
                label={t("Expected lifespan")}
                value={formData.lifespan_pages}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  if (/^\d*$/.test(nextValue)) {
                    setFormData((prev) => ({ ...prev, lifespan_pages: nextValue }));
                  }
                }}
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                InputProps={{ endAdornment: <InputAdornment position="end">{t("pages")}</InputAdornment> }}
                error={Boolean(getFieldError("lifespan_pages"))}
                helperText={getFieldError("lifespan_pages") || t("Leave empty to leave the part out of page costs.")}
              />
            </Box>
            <Box sx={fieldBoxSx}>
              <FormLabel sx={{ fontSize: "0.85rem" }}>{t("Wears with")}</FormLabel>
              <FormGroup row>
                {WEAR_FIELDS.map(([field, label]) => (
                  <FormControlLabel
                    key={field}
                    control={<Checkbox size="small" checked={formData[field]} onChange={handleWearChange(field)} />}
                    label={t(label)}
                  />
                ))}
              </FormGroup>
              <FormHelperText>{t("The pages the lifespan is counted in.")}</FormHelperText>
            </Box>
            <Box sx={{ width: "100%" }}>
              <CustomAutocomplete
                multiple
                size="small"
                apiConfig={{ url: EQUIPMENT_MODELS_ENDPOINT, minSearchChars: 0, debounceMs: 300 }}
                options={modelOptions}
                onOptionsChange={handleModelOptionsChange}
                value={formData.equipment_models}
                onChange={(_, newValue) =>
                  setFormData((prev) => ({ ...prev, equipment_models: (newValue as EquipmentModelOption[]) || [] }))
                }
                isOptionEqualToValue={(option, selectedValue) =>
                  String((option as EquipmentModelOption).id) === String((selectedValue as EquipmentModelOption).id)
                }
                getOptionLabel={(option) =>
                  (option as EquipmentModelOption).display_name || (option as EquipmentModelOption).name || ""
                }
                noOptionsText={t("No options")}
                renderInput={(params) => <TextField {...params} label={t("Used in models")} />}
              />
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" loading={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
